/**
 * 星盘页「开通功能」购买目录（纯逻辑模块）。
 *
 * 数据口径说明：
 * - `key` 与星盘页权限键（PermissionService / 后端 bootstrap）保持一致，用于展示已开通状态；
 * - `type` 是 `configprices.type`，也是微信下单接口 `/yljx/public/pay?type=xxx` 的唯一凭据，
 *   后端 `PayController.applyPurchase()` 的 switch 就是按这个值写 users 表的到期时间列；
 * - `entitlementField` 是 users 表（即登录态 localStorage.user）里对应的到期时间字段，
 *   星盘页判权与这里展示「是否已开通/到期日」用的是同一个字段，不另起一套口径。
 *
 * 本模块只做纯计算，不依赖 window / axios / Vue，便于 node 直跑断言。
 */

/**
 * 14 项可购买权限（顺序即弹窗展示顺序）：
 * 12 项扫码付费 + 点报警(type 39) / 面报警(type 40) 两个 0 元项。
 *
 * 0 元项不走微信下单（微信不接受 0 元金额），点一下由后端 `/yljx/public/permission-claim`
 * 直接开通一年，且未到期时不允许重复领取。
 *
 * 三项配置权益（与其它扫码付费项同一通道，价格在后台 /admin/prices 按 type 配置）：
 *   - 恒星配置权限 type=41：未开通时**打不开恒星配置弹窗**（点击提示并引导到本弹窗付款）；
 *   - 行星配置权限 type=42：同上，管的是行星配置弹窗；
 *   - 逻辑点权限   type=43：同上，管的是逻辑点配置弹窗。
 * 三者都**不是** 0 元项，走正常扫码/JSAPI 支付，不走 permission-claim。
 */
const PERMISSION_PURCHASE_CATALOG = [
  {
    key: 'basic_astrolabe',
    type: '2',
    name: '会员',
    desc: '解锁星盘基础功能与全站会员权益',
    entitlementField: 'express_time'
  },
  {
    key: 'asteroids',
    type: '4',
    name: '虚星',
    desc: '解锁虚星（小行星）数据与盘面显示',
    entitlementField: 'xuxing'
  },
  {
    key: 'planet_power',
    type: '5',
    name: '行星力量',
    desc: '查看行星力量的得分与排序',
    entitlementField: 'liliang'
  },
  {
    key: 'midpoint_formula',
    type: '6',
    name: '催贵公式',
    desc: '解锁中点（催贵公式）计算与标注',
    entitlementField: 'zhongdian'
  },
  {
    key: 'natal_chart',
    type: '7',
    name: '本命盘',
    desc: '解锁本命盘叠加与本命盘数据',
    entitlementField: 'benming'
  },
  {
    key: 'harmonious_phase_search',
    type: '8',
    name: '和谐相位搜索',
    desc: '按和谐相位搜索吉日与用事时段',
    entitlementField: 'hexie'
  },
  {
    key: 'ephemeris_download',
    type: '9',
    name: '星历表下载',
    desc: '下载星历表数据文件',
    entitlementField: 'xiazai'
  },
  {
    key: 'tianyun',
    type: '37',
    name: '天运',
    desc: '解锁天运（世纪运）推运',
    entitlementField: 'tianyun'
  },
  {
    key: 'zuoshan_pointer',
    type: '38',
    name: '座山指针',
    desc: '解锁座山指针动态绘制',
    entitlementField: 'zuoshanzhizhen'
  },
  {
    key: 'point_alarm',
    type: '39',
    name: '点报警',
    desc: '解锁盘面报警恒星点的闪烁提示',
    entitlementField: 'pointAlarm'
  },
  {
    key: 'area_alarm',
    type: '40',
    name: '面报警',
    desc: '解锁整幅盘面的红光报警提示',
    entitlementField: 'areaAlarm'
  },
  {
    key: 'star_control',
    type: '41',
    name: '恒星配置权限',
    desc: '解锁恒星配置弹窗（受管控恒星固定选中、恒参与请求）',
    entitlementField: 'starControl'
  },
  {
    key: 'planet_control',
    type: '42',
    name: '行星配置权限',
    desc: '解锁行星配置弹窗（选择要展示与参与的行星）',
    entitlementField: 'planetControl'
  },
  {
    key: 'logic_point_control',
    type: '43',
    name: '逻辑点权限',
    desc: '解锁逻辑点配置弹窗（选择要展示与参与的逻辑点）',
    entitlementField: 'logicPointControl'
  }
]

/** NATIVE 扫码下单时要求前端显式传价的 type（线上行为：只有 9 星历表下载与 13 私人定制） */
const PRICE_REQUIRED_TYPES = ['9']

function normalizeType (value) {
  return String(value === undefined || value === null ? '' : value).trim()
}

function normalizeText (value) {
  return String(value === undefined || value === null ? '' : value).trim()
}

/**
 * 是否为「有值且可转数字」的入参。
 * 必须显式排除 null/空白：Number(null) === 0，否则会把「没配价」误判成 0 元。
 */
function hasNumberValue (value) {
  if (value === undefined || value === null) {
    return false
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false
  }
  return Number.isFinite(Number(value))
}

/** 目录副本，避免调用方原地改到常量 */
function getPurchaseCatalog () {
  return PERMISSION_PURCHASE_CATALOG.map(item => ({ ...item }))
}

/** 按 key 取单项 */
function findCatalogItem (key) {
  const normalizedKey = normalizeText(key)
  const item = PERMISSION_PURCHASE_CATALOG.find(entry => entry.key === normalizedKey)
  return item ? { ...item } : null
}

/** 把 `/yljx/public/price` 的返回整理成 { type: price } */
function buildPriceMap (priceList) {
  const priceMap = {}
  ;(Array.isArray(priceList) ? priceList : []).forEach(entry => {
    if (!entry || typeof entry !== 'object') {
      return
    }
    const type = normalizeType(entry.type)
    if (!type || !hasNumberValue(entry.price)) {
      return
    }
    const price = Number(entry.price)
    if (!Number.isFinite(price)) {
      return
    }
    priceMap[type] = price
  })
  return priceMap
}

/**
 * 目录与实时价格合并：拿不到价格的项标记为不可购买（弹窗上置灰）。
 * @param {Array} priceList - `/yljx/public/price` 返回的原始数组
 * @param {Array} catalog - 目录，默认用内置 11 项
 * @returns {Array} 合并后的条目
 */
function mergeCatalogWithPrices (priceList, catalog = PERMISSION_PURCHASE_CATALOG) {
  const priceMap = buildPriceMap(priceList)
  return (Array.isArray(catalog) ? catalog : []).map(item => {
    const hasPrice = Object.prototype.hasOwnProperty.call(priceMap, normalizeType(item.type))
    return {
      ...item,
      price: hasPrice ? priceMap[normalizeType(item.type)] : null,
      purchasable: hasPrice
    }
  })
}

/**
 * 用后端目录（`GET /yljx/public/permissionItems`）覆盖本地目录的展示字段与价格。
 *
 * 后端是 key / type / 中文名 / 说明 / 价格的权威来源（改价、改文案只需动后端），
 * 本地目录只补后端不暴露的到期列字段（用于展示「已开通/到期日」），两边以 key 对齐。
 * 后端返回本地没有的 key 时跳过（缺到期字段无法判状态，宁可不展示）。
 *
 * @param {Array} serverItems - 后端目录数组
 * @param {Array} catalog - 本地目录，默认内置 11 项
 * @returns {Array} 合并后的条目；后端无有效数据时返回空数组（调用方据此回退）
 */
function mergeCatalogWithServerItems (serverItems, catalog = PERMISSION_PURCHASE_CATALOG) {
  const list = Array.isArray(serverItems) ? serverItems : []
  if (!list.length) {
    return []
  }
  const localByKey = new Map(catalog.map(item => [item.key, item]))
  const merged = []
  list.forEach(entry => {
    if (!entry || typeof entry !== 'object') {
      return
    }
    const key = normalizeText(entry.key)
    const local = localByKey.get(key)
    if (!key || !local) {
      return
    }
    const price = hasNumberValue(entry.price) ? Number(entry.price) : null
    merged.push({
      ...local,
      type: normalizeType(entry.type) || local.type,
      name: normalizeText(entry.name) || local.name,
      desc: normalizeText(entry.desc) || local.desc,
      price,
      purchasable: price !== null
    })
  })
  return merged
}

/** 到期时间是否仍然有效（与 services/userContext.js 判定口径一致） */
function isEntitlementActive (expiry, now = Date.now()) {
  const text = normalizeText(expiry)
  if (!text) {
    return false
  }
  // 后端返回形如 "yyyy-MM-dd HH:mm:ss"，部分浏览器对 '-' 分隔解析不一致，统一换成 '/'
  const parsedTime = new Date(text.replace(/-/g, '/')).getTime()
  if (!Number.isFinite(parsedTime)) {
    return false
  }
  return now <= parsedTime
}

/** 到期时间只取到「日」，用于徽标下方展示 */
function formatExpiryDate (expiry) {
  const text = normalizeText(expiry)
  if (!text) {
    return ''
  }
  const matched = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (matched) {
    return `${matched[1]}-${matched[2]}-${matched[3]}`
  }
  const parsed = new Date(text.replace(/-/g, '/'))
  if (!Number.isFinite(parsed.getTime())) {
    return ''
  }
  const pad = value => String(value).padStart(2, '0')
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`
}

/**
 * 解析某项权限的开通状态。
 * @param {Object} item - 目录项（需含 entitlementField）
 * @param {Object} user - 登录态里 user 对象（localStorage.user）
 * @param {number} now - 当前时间戳，便于测试注入
 * @returns {{status: string, label: string, expiry: string, expiryDate: string, active: boolean}}
 */
function resolveEntitlementStatus (item, user = {}, now = Date.now()) {
  const rawExpiry = item && item.entitlementField && user ? user[item.entitlementField] : null
  const expiry = normalizeText(rawExpiry)
  const hasExpiry = expiry !== ''
  const active = hasExpiry && isEntitlementActive(expiry, now)

  let status = 'none'
  if (active) {
    status = 'active'
  } else if (hasExpiry) {
    status = 'expired'
  }

  const labelMap = {
    active: '已开通',
    expired: '已过期',
    none: '未开通'
  }

  return {
    status,
    label: labelMap[status],
    expiry: hasExpiry ? expiry : '',
    expiryDate: hasExpiry ? formatExpiryDate(expiry) : '',
    active
  }
}

/** 金额展示：拿不到价格时明确提示，不显示 ￥0.00 误导用户 */
function formatPrice (price) {
  if (!hasNumberValue(price)) {
    return '待定'
  }
  return `￥${Number(price).toFixed(2)}`
}

/**
 * 是否为「0 元项」：有配价且价格恰为 0。
 *
 * 必须区分 null（没配价 = 暂不可购买）与 0（免费），混同会让未配价的项被误判成可免费领。
 * 与后端 `PermissionCatalog.isFreePrice` 同一口径，价格以 `permissionItems` 实时返回为准，
 * 因此后台把价格改回非 0 时，前端也会自动退回扫码支付流程。
 */
function isFreeItem (item) {
  if (!item || !hasNumberValue(item.price)) {
    return false
  }
  return Number(item.price) === 0
}

/**
 * 0 元项此刻是否还能「点一下开通」。
 *
 * 已开通未到期（status.active）→ false：对应需求里「首次点击后按钮不可再点」，
 * 再点也不允许修改或延长已开通的年份（服务端同样会拦，前端只是不给入口）。
 *
 * @param {Object} item - 合并后的目录项（含 price，通常已带 status）
 * @param {number} now - 当前时间戳，便于测试注入
 */
function canClaimFreeItem (item, now = Date.now()) {
  if (!isFreeItem(item)) {
    return false
  }
  const status = item.status && typeof item.status === 'object'
    ? item.status
    : resolveEntitlementStatus(item, {}, now)
  return !status.active
}

/**
 * 组装下单参数（NATIVE 与 JSAPI 都用同一组）。
 * @param {Object} item - 目录项
 * @param {string|number} userId - 用户 id（users.id）
 * @param {string} style - 'native' | 'jsapi'
 */
function buildPurchaseParams (item, userId, style = 'native') {
  const type = normalizeType(item && item.type)
  const params = { type, id: userId }

  // 只有 NATIVE 扫码的 type=9 需要前端传价；JSAPI 一律由后端按 type 查价。
  const needsPrice = style === 'native' && PRICE_REQUIRED_TYPES.includes(type)
  const price = item ? item.price : null
  if (needsPrice && price !== null && price !== undefined) {
    params.price = price
  }
  return params
}

/**
 * 组装 0 元项「自助开通」参数（`/yljx/public/permission-claim`）。
 * 后端会重新按 type 查 configprices 校验「确实是 0 元项」，前端传价无效也不可信。
 * @param {Object} item - 目录项
 * @param {string|number} userId - 用户 id（users.id）
 */
function buildClaimParams (item, userId) {
  return { type: normalizeType(item && item.type), id: userId }
}

/** 支付轮询结果判定：后端成功时返回 code=1111 且 data 为最新 user */
function resolvePaidUser (response) {
  if (!response || typeof response !== 'object') {
    return null
  }
  if (response.code !== '1111') {
    return null
  }
  const user = response.data
  return user && typeof user === 'object' ? user : null
}

/** 目录项 key → 登录态到期字段名（找不到返回空串） */
function getEntitlementField (key, catalog = PERMISSION_PURCHASE_CATALOG) {
  const item = findCatalogItem(key, catalog)
  return item ? normalizeText(item.entitlementField) : ''
}

/**
 * 某个权益 key 当前是否有效。
 *
 * 用于「配置弹窗入口判权」：未开通对应管控权益时不开弹窗、改为引导付款。
 * 口径与 services/userContext.isEntitlementActive 完全一致（读登录态 user 的到期字段，
 * 未登录 / 无字段 / 已过期 都算无权限），保证「入口判权」和「弹窗内展示的已开通状态」不打架。
 *
 * @param {string} key - 目录 key（如 star_control / planet_control / logic_point_control）
 * @param {Object} user - 登录态 user 对象（localStorage.user）
 * @param {number} now - 当前时间戳，便于测试注入
 * @param {Array} catalog - 本地目录（可注入测试目录）
 */
function isPermissionActive (key, user, now = Date.now(), catalog = PERMISSION_PURCHASE_CATALOG) {
  const field = getEntitlementField(key, catalog)
  if (!field || !user || typeof user !== 'object') {
    return false
  }
  return isEntitlementActive(user[field], now)
}

module.exports = {
  PERMISSION_PURCHASE_CATALOG,
  PRICE_REQUIRED_TYPES,
  getPurchaseCatalog,
  findCatalogItem,
  getEntitlementField,
  isPermissionActive,
  buildPriceMap,
  mergeCatalogWithPrices,
  mergeCatalogWithServerItems,
  isEntitlementActive,
  formatExpiryDate,
  resolveEntitlementStatus,
  formatPrice,
  isFreeItem,
  canClaimFreeItem,
  buildPurchaseParams,
  buildClaimParams,
  resolvePaidUser
}
